<template>
  <teleport to="body"
    ><div class="ui-snackbar__overlay">
      <transition name="ui-snackbar-transition">
        <div v-if="model" class="ui-snackbar__wrapper">
          <div class="ui-snackbar" :class="selector">
            <div class="ui-snackbar__content">
              <slot></slot>
            </div>
          </div>
        </div>
      </transition></div
  ></teleport>
</template>
<script lang="ts" setup>
import { computed, onUnmounted, watch, type PropType } from 'vue'

// Props
const props = defineProps({
  type: {
    type: String as PropType<'error' | 'warning' | 'success' | null>,
    default: null,
  },
  timeout: {
    type: [String, Number],
    default: 5000,
  },
})

let timerId: ReturnType<typeof setTimeout>
const model = defineModel<boolean>()

// Computed
const selector = computed(() => {
  return {
    [`_${props.type}`]: !!props.type,
  }
})

const timeout = computed(() => Number(props.timeout))

// Watchers
watch(model, (value) => (value ? setTimer() : clearTimer()))

// Methods
function clearTimer() {
  if (timerId) {
    clearTimeout(timerId)
  }
}

function setTimer() {
  if (timeout.value <= 0) {
    return
  }

  timerId = setTimeout(() => {
    model.value = false
  }, timeout.value)
}

// Hooks
onUnmounted(() => clearTimer)
</script>
<style lang="sass" scoped>
@use '@/styles/variables'
@use '@/styles/mixins'
@use 'sass:map'

.ui-snackbar
    display: flex
    overflow: hidden
    align-items: center
    padding: variables.$spacer*1.5
    pointer-events: auto
    border-radius: map.get(variables.$border-radius, large)
    background-color: map.get(variables.$colors, success, default)
    color: map.get(variables.$text-color, accent)
    min-width: 250px
    box-shadow: map.get(variables.$box-shadow, default)
    user-select: none

    &__overlay
        position: fixed
        pointer-events: none
        display: flex
        inset: 0
        justify-content: center
        align-items: flex-end
        margin: variables.$spacer*3

// transition
.ui-snackbar-transition-enter-active
    +mixins.transition(all, ease-out)

.ui-snackbar-transition-leave-active
    +mixins.transition(all, ease-in)

.ui-snackbar-transition-enter-from
    transform: translateX(calc(-50px - 50%))
    opacity: 0

.ui-snackbar-transition-leave-to
    transform: translateX(calc(50px - 50%))
    opacity: 0
</style>
